import React from "react"
import {useTranslation} from "react-i18next"

import RankRocketBronze from "../../../assets/rank-rocket-bronze.svg?react"
import RankRocketGold from "../../../assets/rank-rocket-gold.svg?react"
import RankRocketSilver from "../../../assets/rank-rocket-silver.svg?react"
import {ProfileImage} from "../../../components/ProfileImage.tsx"
import {Tooltip} from "../../../components/Tooltip.tsx"
import {ASalesAssistantSalesPerson} from "../../../services/types.generated.ts"

type LeaderboardPodiumProps = {
  topThree: ASalesAssistantSalesPerson[]
}

export const LeaderboardPodium: React.FC<LeaderboardPodiumProps> = ({topThree}) => {
  const {t} = useTranslation()
  const second = topThree?.[1]
  const first = topThree?.[0]
  const third = topThree?.[2]

  return (
    <div className={"inline-flex items-end justify-start gap-0 self-center md:gap-0"}>
      <div className={"inline-flex flex-col items-center justify-start"}>
        {second && (
          <>
            <div className={"relative mb-2 md:mb-4"}>
              <RankRocketSilver className={"absolute -top-[5px] -right-[5px] h-6 w-6 md:h-8 md:w-8"} />
              <Tooltip
                buttonNode={
                  <ProfileImage
                    src={second?.profile_picture_thumbnail_url ?? undefined}
                    alt={
                      second
                        ? `${second.first_name} ${second.last_name}`
                        : t("SalesAssistant_Leaderboard_Podium_SecondPlace")
                    }
                    className={"h-14 w-14 sm:h-16 sm:w-16 md:h-[85px] md:w-[85px]"}
                  />
                }
              >
                <div className={"rounded-lg bg-gray-900/90 px-2 py-2 text-sm text-cr-white"}>
                  {`${second.first_name} ${second.last_name}`}
                </div>
              </Tooltip>
            </div>
            <div className={"mb-1 self-center rounded-lg bg-indigo-200 px-1.5 py-[2px] md:mb-2 md:px-2 md:py-[3px]"}>
              <div className={"justify-start text-center"}>
                <span className={"font-['Sora'] text-xs font-bold text-indigo-500 sm:text-sm md:text-base"}>
                  {second?.sales_assistant_updates ?? 0}
                </span>
                <span className={"font-['Sora'] text-xs font-normal text-indigo-500 sm:text-sm md:text-base"}>
                  /{second?.total_updates ?? 0}
                </span>
              </div>
            </div>
          </>
        )}
        <svg viewBox={"0 0 129 13"} fill={"none"} xmlns={"http://www.w3.org/2000/svg"} className={"w-full"}>
          <path d={"M24 0H129V13H0L24 0Z"} fill={"var(--color-cr-blue-mid-light)"} />
        </svg>
        <div
          className={
            "flex h-16 items-center justify-center self-stretch bg-linear-143 from-indigo-400 to-indigo-100 sm:h-20 md:h-28"
          }
        >
          <div className={"justify-start font-['Sora'] text-4xl font-bold text-white sm:text-5xl md:text-7xl"}>2</div>
        </div>
      </div>

      <div className={"inline-flex flex-col items-center justify-center"}>
        {first && (
          <>
            <div className={"relative mb-2 md:mb-4"}>
              <RankRocketGold className={"absolute -top-[5px] -right-[5px] h-6 w-6 md:h-8 md:w-8"} />
              <Tooltip
                buttonNode={
                  <ProfileImage
                    src={first?.profile_picture_thumbnail_url ?? undefined}
                    alt={
                      first
                        ? `${first.first_name} ${first.last_name}`
                        : t("SalesAssistant_Leaderboard_Podium_FirstPlace")
                    }
                    className={"h-14 w-14 sm:h-16 sm:w-16 md:h-[85px] md:w-[85px]"}
                  />
                }
              >
                <div className={"rounded-lg bg-gray-900/90 px-2 py-2 text-sm text-cr-white"}>
                  {`${first.first_name} ${first.last_name}`}
                </div>
              </Tooltip>
            </div>
            <div className={"mb-1 self-center rounded-lg bg-indigo-200 px-1.5 py-[2px] md:mb-2 md:px-2 md:py-[3px]"}>
              <div className={"justify-start text-center"}>
                <span className={"font-['Sora'] text-xs font-bold text-indigo-500 sm:text-sm md:text-base"}>
                  {first?.sales_assistant_updates ?? 0}
                </span>
                <span className={"font-['Sora'] text-xs font-normal text-indigo-500 sm:text-sm md:text-base"}>
                  /{first?.total_updates ?? 0}
                </span>
              </div>
            </div>
          </>
        )}
        <svg viewBox={"0 0 141 13"} fill={"none"} xmlns={"http://www.w3.org/2000/svg"} className={"w-full"}>
          <path d={"M26.2326 0H114.767L141 13H0L26.2326 0Z"} fill={"var(--color-cr-blue-mid-light)"} />
        </svg>
        <div
          className={
            "flex h-20 items-center justify-center self-stretch bg-linear-134 from-indigo-500 to-indigo-200 sm:h-24 md:h-32"
          }
        >
          <div className={"justify-start font-['Sora'] text-4xl font-bold text-white sm:text-5xl md:text-7xl"}>1</div>
        </div>
      </div>

      <div className={"inline-flex flex-col items-center justify-start"}>
        {third && (
          <>
            <div className={"relative mb-2 md:mb-4"}>
              <RankRocketBronze className={"absolute -top-[5px] -right-[5px] h-6 w-6 md:h-8 md:w-8"} />
              <Tooltip
                buttonNode={
                  <ProfileImage
                    src={third?.profile_picture_thumbnail_url ?? undefined}
                    alt={
                      third
                        ? `${third.first_name} ${third.last_name}`
                        : t("SalesAssistant_Leaderboard_Podium_ThirdPlace")
                    }
                    className={"h-14 w-14 sm:h-16 sm:w-16 md:h-[85px] md:w-[85px]"}
                  />
                }
              >
                <div className={"rounded-lg bg-gray-900/90 px-2 py-2 text-sm text-cr-white"}>
                  {`${third.first_name} ${third.last_name}`}
                </div>
              </Tooltip>
            </div>
            <div className={"mb-1 self-center rounded-lg bg-indigo-200 px-1.5 py-[2px] md:mb-2 md:px-2 md:py-[3px]"}>
              <div className={"justify-start text-center"}>
                <span className={"font-['Sora'] text-xs font-bold text-indigo-500 sm:text-sm md:text-base"}>
                  {third?.sales_assistant_updates ?? 0}
                </span>
                <span className={"font-['Sora'] text-xs font-normal text-indigo-500 sm:text-sm md:text-base"}>
                  /{third?.total_updates ?? 0}
                </span>
              </div>
            </div>
          </>
        )}
        <svg viewBox={"0 0 129 13"} fill={"none"} xmlns={"http://www.w3.org/2000/svg"} className={"w-full"}>
          <path d={"M0 0H105L129 13H0V0Z"} fill={"var(--color-cr-blue-mid-light)"} />
        </svg>
        <div
          className={
            "flex h-12 items-center justify-center self-stretch bg-linear-143 from-indigo-400 to-indigo-100 sm:h-16 md:h-20"
          }
        >
          <div className={"justify-start font-['Sora'] text-4xl font-bold text-white sm:text-5xl md:text-7xl"}>3</div>
        </div>
      </div>
    </div>
  )
}
